/**
 * paymentMethodIncentive.ts — Incentivo (descuento al cliente) por método de pago.
 *
 * Qué hace:
 *  - Lee el % de incentivo configurado por método (`paymentMethodsConfig`).
 *  - Calcula el descuento por ítem (cada línea neta baja (1 − pct/100), redondeada
 *    a pesos) y el total con incentivo.
 *  - Provee enriquecimiento de snapshot para venta/pedido (anti-fantasma) y
 *    labels reutilizables para UI y tickets.
 *
 * Reglas:
 *  - Solo aplica en Pago Simple (un método cubre toda la venta). Dividido: no.
 *  - Efectivo: el redondeo final a la menor denominación lo hace el pipeline de
 *    checkout existente (`resolveCheckoutTotals` → `alignTotalForEfectivo`).
 *  - El incentivo convive con la tarifa de pasarela: reduce la base; la tarifa
 *    (negocio/cliente) se calcula sobre el total ya descontado.
 *
 * NO debe: importar React, leer localStorage ni mutar ventas (funciones puras).
 */

import type {
  PaymentMethodsConfig,
  PaymentMethodSetting,
} from "@/lib/paymentMethodsConfig";
import { getIncentivePctForMethod } from "@/lib/paymentMethodsConfig";
import type { PaymentBreakdown, Venta } from "../types/caja.types";
import type { TicketItem } from "../components/TicketItemsTable";

export { getIncentivePctForMethod };

/** Línea base (neta tras descuentos de ítem/global) para aplicar el incentivo. */
export interface IncentiveBaseLine {
  lineId: string;
  nombre: string;
  /** Neto de la línea (subtotal de la línea tras descuentos existentes). */
  neto: number;
  cantidad: number;
}

/** Línea con el incentivo aplicado (precios y montos ya descontados). */
export interface IncentiveLineResult {
  lineId: string;
  nombre: string;
  cantidad: number;
  netoOriginal: number;
  netoConIncentivo: number;
  descuentoLinea: number;
  precioUnitarioOriginal: number;
  precioUnitarioConIncentivo: number;
}

/** Resumen del incentivo activo en el cobro/pedido. */
export interface IncentiveSummary {
  pct: number;
  metodoId: string;
  metodoLabel: string;
  descuentoTotal: number;
  subtotalOriginal: number;
  subtotalConIncentivo: number;
  /** Líneas del carrito con su neto ya descontado (para UI/ticket por ítem). */
  lines: IncentiveLineResult[];
}

/** Formatea el % para UI/tickets (ej: 10 → "10%", 7.5 → "7,5%"). */
export function formatIncentivePct(pct: number): string {
  const safe = Math.min(100, Math.max(0, Math.round(pct * 100) / 100));
  return safe % 1 === 0
    ? `${safe}%`
    : `${safe.toLocaleString("es-CO", { maximumFractionDigits: 2 })}%`;
}

/** Etiqueta corta para chips/badges (ej: "−10%"). */
export function buildIncentiveChipLabel(pct: number): string {
  return `−${formatIncentivePct(pct)}`;
}

/** Etiqueta para línea de totales/ticket (ej: "Incentivo por pagar con Efectivo (−10%)"). */
export function buildIncentiveTicketLabel(
  methodLabel: string,
  pct: number,
): string {
  return `Incentivo por pagar con ${methodLabel} (${formatIncentivePct(pct)})`;
}

/** Etiqueta de la sub-línea por ítem (ej: "Descuento por Efectivo (10%)"). */
export function buildIncentiveItemLineLabel(
  methodLabel: string,
  pct: number,
): string {
  return `Descuento por ${methodLabel} (${formatIncentivePct(pct)})`;
}

const roundCop = (n: number): number => Math.max(0, Math.round(n));

/** Aplica el incentivo a cada línea base (redondeo por ítem a pesos). */
export function computeIncentiveLines(
  bases: IncentiveBaseLine[],
  pct: number,
): IncentiveLineResult[] {
  if (!bases?.length || pct <= 0) return [];
  const factor = 1 - Math.min(100, pct) / 100;
  return bases.map((b) => {
    const cantidad = Math.max(1, Math.round(b.cantidad || 1));
    const netoOriginal = roundCop(b.neto);
    const netoConIncentivo = roundCop(netoOriginal * factor);
    const unitOriginal = Math.round(netoOriginal / cantidad);
    return {
      lineId: b.lineId,
      nombre: b.nombre,
      cantidad,
      netoOriginal,
      netoConIncentivo,
      descuentoLinea: netoOriginal - netoConIncentivo,
      precioUnitarioOriginal: unitOriginal,
      precioUnitarioConIncentivo: Math.round(
        (netoOriginal / cantidad) * factor,
      ),
    };
  });
}

/** Resumen del incentivo para un subtotal neto (fallback sin líneas). */
export function computeIncentiveOnSubtotal(
  subtotal: number,
  pct: number,
): { descuento: number; subtotalConIncentivo: number } {
  const base = roundCop(subtotal);
  if (base <= 0 || pct <= 0)
    return { descuento: 0, subtotalConIncentivo: base };
  const descuento = roundCop((base * Math.min(100, pct)) / 100);
  return { descuento, subtotalConIncentivo: base - descuento };
}

/** Enriquece la línea principal del breakdown con el snapshot de incentivo. */
export function enrichBreakdownWithIncentive(
  lines: PaymentBreakdown[],
  summary: IncentiveSummary | null,
): PaymentBreakdown[] {
  if (!summary || summary.descuentoTotal <= 0 || lines.length === 0)
    return lines;
  const out = [...lines];
  const first = out[0];
  if (!first) return out;
  out[0] = {
    ...first,
    incentivePct: summary.pct,
    incentiveAmount: summary.descuentoTotal,
  };
  return out;
}

/**
 * Aplica el incentivo a los `TicketItem`: la fila principal conserva el PRECIO
 * UNITARIO original (P.UNIT de lista) y baja su subtotal de línea al neto con
 * incentivo; debajo se agrega una sub-línea de descuento por ítem
 * («Descuento por [método] (X%)») con su monto, igual que el descuento por ítem.
 */
export function applyIncentiveToTicketItems(
  ticketItems: TicketItem[],
  pct: number,
  metodoLabel?: string,
): TicketItem[] {
  if (!ticketItems?.length || pct <= 0) return ticketItems;
  const factor = 1 - Math.min(100, pct) / 100;
  const out: TicketItem[] = [];
  for (const row of ticketItems) {
    if (row.isSubLine || row.isDiscountLine || row.isTaxLine) {
      out.push(row);
      continue;
    }
    const qty = Math.max(1, row.cantidad || 1);
    const subtotal =
      row.lineSubtotal != null
        ? row.lineSubtotal
        : Math.round(row.precioUnitario * qty);
    const netoConIncentivo = roundCop(subtotal * factor);
    const descuentoLinea = Math.max(0, subtotal - netoConIncentivo);
    out.push({ ...row, lineSubtotal: netoConIncentivo });
    if (descuentoLinea > 0) {
      out.push({
        nombre: buildIncentiveItemLineLabel(metodoLabel || "método", pct),
        cantidad: 0,
        precioUnitario: 0,
        lineSubtotal: -descuentoLinea,
        isSubLine: true,
        isDiscountLine: true,
        parentNombre: row.nombre,
      });
    }
  }
  return out;
}

/**
 * Aplica el incentivo al snapshot `Venta["descuentos"]` para que devoluciones,
 * historial y cierre repliquen el precio real cobrado (con incentivo).
 */
export function applyIncentiveToDescuentosSnapshot(
  descuentos: NonNullable<Venta["descuentos"]>,
  summary: IncentiveSummary | null,
): NonNullable<Venta["descuentos"]> {
  if (!summary || summary.descuentoTotal <= 0 || !descuentos) return descuentos;
  const factor = 1 - Math.min(100, summary.pct) / 100;
  const withIncentivo = {
    ...descuentos,
    incentivoMetodoPago: {
      metodoId: summary.metodoId,
      metodoLabel: summary.metodoLabel,
      pct: summary.pct,
      descuentoTotal: summary.descuentoTotal,
      subtotalAntesIncentivo: summary.subtotalOriginal,
    },
  };
  const lineDetails = descuentos.lineDetails?.map((l) => ({
    ...l,
    netoFinal: roundCop(l.netoFinal * factor),
    precioUnitarioFinal: roundCop(l.precioUnitarioFinal * factor),
  }));
  const lines = descuentos.breakdownSnapshot?.lines.map((l) => ({
    ...l,
    netoItem: roundCop(l.netoItem * factor),
    precioUnitarioFinal:
      l.precioUnitarioFinal != null
        ? roundCop(l.precioUnitarioFinal * factor)
        : l.precioUnitarioFinal,
  }));
  return {
    ...withIncentivo,
    ...(lineDetails ? { lineDetails } : {}),
    ...(descuentos.breakdownSnapshot && lines
      ? { breakdownSnapshot: { ...descuentos.breakdownSnapshot, lines } }
      : {}),
  };
}

/** ¿Un método de la config tiene incentivo activo? (para badges y avisos). */
export function methodHasIncentive(
  method: Pick<PaymentMethodSetting, "incentivePct"> | undefined,
): boolean {
  return (
    typeof method?.incentivePct === "number" &&
    Number.isFinite(method.incentivePct) &&
    (method.incentivePct as number) > 0
  );
}

/** Resuelve el % de incentivo de un método dado el config (0 si no aplica). */
export function resolveIncentivePct(
  methodId: string,
  cfg?: PaymentMethodsConfig | null,
): number {
  return getIncentivePctForMethod(methodId, cfg);
}
